import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/Modals";
import { linkMyTelegram, myTelegramLink } from "@/lib/integrationsApi";

const reasonOf = err => {
  const detail = err?.response?.data?.detail;
  return typeof detail === "string" ? detail : err?.response?.status ? `the server answered ${err.response.status}` : "the server could not be reached";
};

/**
 * Profile → Connect my Telegram (Telegram Phase 1, PRD §4.2). Anyone signed in
 * links their own chat; it grants nothing — the bot shows what their role and
 * permissions already allow. A phone opens Telegram straight away; a PC shows a
 * QR for the phone. The dialog watches for the link and says "Connected" the
 * moment they tap Start.
 */
export function ConnectMyTelegramDialog({ token, onClose }) {
  const [state, setState] = useState({ phase: "loading" });

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const now = await myTelegramLink(token);
        if (!live) return;
        if (now.linked) { setState({ phase: "linked", link: now }); return; }
        const issued = await linkMyTelegram(token);
        const qr = await QRCode.toDataURL(issued.link, { margin: 1, width: 220 });
        if (live) setState({ phase: "issued", issued, qr });
      } catch (err) {
        if (live) setState({ phase: "error", message: reasonOf(err) });
      }
    })();
    return () => { live = false; };
  }, [token]);

  // Poll while the QR is up: the bot links on Start, in another app.
  useEffect(() => {
    if (state.phase !== "issued") return undefined;
    const timer = window.setInterval(async () => {
      try {
        const now = await myTelegramLink(token);
        if (now.linked) setState({ phase: "linked", link: now });
      } catch { /* the next tick asks again; the QR stays usable */ }
    }, 3000);
    return () => window.clearInterval(timer);
  }, [state.phase, token]);

  return (
    <Modal title="Connect my Telegram" onClose={onClose} testId="connect-my-telegram">
      {state.phase === "loading" && <p className="text-body text-muted-foreground">Making your link…</p>}
      {state.phase === "error" && (
        <p className="text-body text-danger" role="alert" data-testid="connect-my-telegram-error">
          Could not make your Telegram link: {state.message}.
        </p>
      )}
      {state.phase === "linked" && (
        <p className="text-body" data-testid="connect-my-telegram-linked">
          Connected{state.link.chat_title ? ` as ${state.link.chat_title}` : ""}. The bot shows what your Vibez access allows.
        </p>
      )}
      {state.phase === "issued" && (
        <div className="flex flex-col items-center gap-snug text-center" data-testid="connect-my-telegram-issued">
          <Button variant="action" asChild className="w-full">
            <a href={state.issued.link} target="_blank" rel="noreferrer" data-testid="connect-my-telegram-open">Open Telegram</a>
          </Button>
          <p className="text-meta text-muted-foreground">On a computer? Scan this with your phone camera.</p>
          <img src={state.qr} alt="Telegram link QR code" className="w-48 h-48 rounded border bg-white" />
          <p className="text-meta">
            Tap <b>Start</b> in Telegram. Works once, until{" "}
            {new Date(state.issued.expires_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}.
          </p>
          <p className="text-micro text-muted-foreground">Waiting for Start…</p>
        </div>
      )}
    </Modal>
  );
}

export default function ConnectMyTelegramButton({ token, className = "", iconOnly = false, testId = "connect-my-telegram-button" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={className} data-testid={testId}
              aria-label="Connect my Telegram" title="Connect my Telegram">
        <Send className={iconOnly ? "w-4 h-4" : "w-5 h-5"} aria-hidden />
        {!iconOnly && "Connect my Telegram"}
      </button>
      {open && <ConnectMyTelegramDialog token={token} onClose={() => setOpen(false)} />}
    </>
  );
}
